// Settings → Network of a deployment (spec 11 6.15.14, mock n06): standalone or attached, the network and size, the
// addresses it will get from the dry run's `networkImpact`, what attaching does, detach and release.
import { RequestTargets } from '../requests/DeploymentRequests';
import { Network as NetworkIcon, TriangleAlert } from 'lucide-react';
import { useEffect, useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Versioned } from '../../api/client';
import { useNetworkPage } from '../../api/queries';
import type {
  AttachmentSize,
  Deployment,
  DeploymentNetworkImpact,
  Diagnostic,
  NetworkListItem,
  RunSummary,
} from '../../api/types';
import { StatusBadge } from '../../components/ui';
import type { SettingsForm } from '../../deployments/settingsForm';
import { strings } from '../../strings';
import {
  attachmentPresentation,
  networkErrorInfo,
  providerLabel,
  roleCapacities,
  roleSubnets,
  SIZE_ORDER,
  sizeOptionLabel,
  useManageNetworks,
  useNetworking,
} from '../shared';
import { NetworkProblemCallout } from './NetworkProblem';
import { ReleaseAddressesButton } from './ReleaseAddresses';

const s = strings.networks.deployment;
const n = strings.networks;

/** The (provider, region) key of `settings.network.sizes`. */
const sizeKey = (provider: string, region: string) => `${provider}/${region}`;

/** The largest of the regions' recommended sizes (6.15.14). */
function recommendedSize(impact: DeploymentNetworkImpact | null | undefined): AttachmentSize | null {
  const sizes = (impact?.regions ?? []).map((r) => r.recommendedSize);
  if (!sizes.length) return null;
  return sizes.reduce((a, b) => (SIZE_ORDER.indexOf(b) > SIZE_ORDER.indexOf(a) ? b : a));
}

/** Every region's estimated need per role, the highest of each role. */
function needsOf(impact: DeploymentNetworkImpact | null | undefined): Record<string, number> {
  const needs: Record<string, number> = {};
  for (const region of impact?.regions ?? [])
    for (const [role, need] of Object.entries(region.needs ?? {}))
      needs[role] = Math.max(needs[role] ?? 0, need);
  return needs;
}

/** The role with the highest need for its capacity in a size, with both numbers. */
function tightestRole(needs: Record<string, number>, size: AttachmentSize) {
  const capacities = roleCapacities(size);
  let best: { role: string; need: number; capacity: number } | null = null;
  for (const [role, need] of Object.entries(needs)) {
    const capacity = capacities[role];
    if (!capacity) continue;
    if (!best || need / capacity > best.need / best.capacity) best = { role, need, capacity };
  }
  return best;
}

function whereText(item: Pick<NetworkListItem, 'providers'>) {
  return item.providers.map((p) => `${providerLabel(p.provider)} ${p.regions.join(', ')}`).join(' · ');
}

/** The settings form's network part (spec 11 6.15.14). */
export function NetworkSection({
  deployment,
  form,
  setForm,
  impact,
  impactPending,
  impactFailed,
  diagnostics,
  problem,
  detach,
  operate,
  onReleased,
}: {
  /** The stored deployment (the form's base). */
  deployment: Deployment;
  form: SettingsForm;
  setForm: (update: (form: SettingsForm) => SettingsForm) => void;
  /** The dry run's `networkImpact` (null: no change; undefined: not answered yet). */
  impact: DeploymentNetworkImpact | null | undefined;
  impactPending: boolean;
  impactFailed: boolean;
  /** The network diagnostics of the dry run (impact and settings ones). */
  diagnostics: readonly Diagnostic[];
  /** A Problem of the last save about the network. */
  problem: { code: string; message: string; details?: unknown } | null;
  /** After a detach was saved: the network and the two runs from the response's `runs`. */
  detach: { network: string; runs: RunSummary[] } | null;
  operate: boolean;
  onReleased: (deployment: Versioned<Deployment>) => void;
}) {
  const ids = useId();
  const networking = useNetworking();
  const admin = useManageNetworks();
  const networks = useNetworkPage(null, networking.enabled);
  const items = networks.data?.items ?? [];
  const dev = deployment.name;
  const current = deployment.network ?? null;
  const attachedTo = current?.mode === 'attached' ? current : null;
  const retainedBy = current?.mode === 'retained' ? current : null;
  const runActive = !!deployment.activeRunId;
  const editable = operate && networking.enabled;
  const disabledReason = runActive ? s.runActive : null;
  const chosen = form.network;
  const chosenItem = chosen ? items.find((x) => x.id === chosen.networkId) : undefined;
  const chosenName = chosenItem?.name ?? (chosen && current?.id === chosen.networkId ? current.name : '');

  // Options the user can't choose, and why (the UI never sends those PATCHes).
  const reasonFor = (item: NetworkListItem): string | null => {
    if (item.id === current?.id) return null;
    if (attachedTo) return s.reasons.changeNeedsDetach(attachedTo.name, dev);
    if (retainedBy) return s.reasons.retained(dev, retainedBy.name);
    if (item.status === 'destroyed') return s.reasons.destroyed(item.name);
    if (item.attachPolicy === 'network-admins' && !admin) return s.reasons.adminsOnly(item.name);
    return null;
  };
  const unavailable = items.map((item) => ({ item, reason: reasonFor(item) })).filter((x) => x.reason);

  const recommended = recommendedSize(impact);
  const needs = needsOf(impact);
  const changedNetwork = !!chosen && chosen.networkId !== form.originalNetwork?.networkId;
  // A newly chosen network takes the recommended size once the dry run knows it.
  // Only while the size is still unset: setForm also clears the form's server problems, so calling it on every render
  // (its identity changes with each one) would never settle.
  const unsetSize = chosen?.size === null;
  // Feature spec 17, phase 2 (6.15.22): a request's suggested target is preselected but unsaved (the save bar marks it).
  const missingSuggestions = (impact?.requestChoices ?? []).filter(
    (c) => c.suggested && !chosen?.requestTargets?.[c.requestId],
  );
  const suggestionKey = missingSuggestions.map((c) => `${c.requestId}=${c.suggested}`).join(',');
  useEffect(() => {
    if (!editable || !suggestionKey) return;
    const picks = Object.fromEntries(suggestionKey.split(',').map((x) => x.split('=')));
    setForm((f) =>
      f.network
        ? { ...f, network: { ...f.network, requestTargets: { ...picks, ...f.network.requestTargets } } }
        : f,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestionKey, editable]);
  useEffect(() => {
    if (!changedNetwork || !recommended || !unsetSize || impact?.networkId !== chosen?.networkId) return;
    setForm((f) =>
      f.network && f.network.size === null ? { ...f, network: { ...f.network, size: recommended } } : f,
    );
  }, [changedNetwork, recommended, unsetSize, impact?.networkId, chosen?.networkId, setForm]);

  const size = chosen?.size ?? recommended ?? null;
  const tight = recommended ? tightestRole(needs, recommended) : null;
  const tooSmall =
    size && recommended && SIZE_ORDER.indexOf(size) < SIZE_ORDER.indexOf(recommended)
      ? Object.entries(needs)
          .map(([role, need]) => ({ role, need, capacity: roleCapacities(size)[role] ?? Infinity }))
          .find((x) => x.need > x.capacity)
      : undefined;

  const setChoice = (attach: boolean) => {
    if (disabledReason) return;
    setForm((f) => ({
      ...f,
      network: attach
        ? (f.network ??
          (f.originalNetwork
            ? {
                networkId: f.originalNetwork.networkId,
                size: f.originalNetwork.size ?? null,
                sizes: { ...(f.originalNetwork.sizes ?? {}) },
              }
            : retainedBy
              ? { networkId: retainedBy.id, size: null, sizes: {} }
              : { networkId: '', size: null, sizes: {} }))
        : null,
    }));
  };
  const setNetwork = (networkId: string) => {
    if (disabledReason) return;
    const item = items.find((x) => x.id === networkId);
    if (item && reasonFor(item)) return;
    setForm((f) => ({ ...f, network: { networkId, size: null, sizes: {} } }));
  };
  const setSize = (value: AttachmentSize) => {
    if (disabledReason || !chosen) return;
    setForm((f) => (f.network ? { ...f, network: { ...f.network, size: value } } : f));
  };
  const setRegionSize = (key: string, value: AttachmentSize | '') => {
    if (disabledReason || !chosen) return;
    setForm((f) => {
      if (!f.network) return f;
      const sizes = { ...f.network.sizes };
      if (value) sizes[key] = value;
      else delete sizes[key];
      return { ...f, network: { ...f.network, sizes } };
    });
  };

  // ---- read-only views
  if (!networking.enabled) {
    if (!current) return null;
    return (
      <div data-testid="network-section-off">
        <dl className="settings-dl">
          <dt>{s.network}</dt>
          <dd>{current.name}</dd>
          <dt>{s.modeLabel}</dt>
          <dd>{s.mode[current.mode]}</dd>
        </dl>
        <p className="callout callout-info mt-3">{s.offReadOnly(dev)}</p>
      </div>
    );
  }

  const shownDiagnostics = diagnostics.filter(
    (d) => d.code !== 'IPAM_SUBNET_TOO_SMALL' && d.code !== 'NETWORK_PROVIDER_NOT_ENABLED',
  );
  const radioName = `${ids}-mode`;
  const reasonId = `${ids}-reason`;
  const unavailableId = `${ids}-unavailable`;
  const detaching = !!attachedTo && !chosen;

  const regionsWithBlocks = (impact?.regions ?? []).filter((r) => r.cidr);
  const showImpact = !!impact && impact.change !== 'detach' && impact.change !== 'release' && !!chosen;
  const roleOrder = Object.keys(needs).length ? Object.keys(needs) : null;
  const subnetText = (cidr: string) =>
    roleSubnets(cidr)
      .filter((r) => !roleOrder || roleOrder.includes(r.role))
      .map((r) => `${r.role} ${r.cidr}`)
      .join(' · ');
  const staysReason = (provider: string, region: string) => {
    const where = chosenItem?.providers.find((p) => p.provider === provider);
    if (!where) return s.notUsed(chosenName, providerLabel(provider));
    if (!where.regions.includes(region)) return s.notIncluded(chosenName, region);
    return null;
  };

  return (
    <div data-testid="network-section">
      {problem && (
        <NetworkProblemCallout
          code={problem.code}
          message={problem.message}
          details={problem.details}
          networkId={chosen?.networkId || current?.id}
          context={{ network: chosenName || current?.name, deployment: dev }}
          deploymentId={deployment.id}
        />
      )}

      {retainedBy && (
        <div className="callout callout-info mb-3" data-testid="network-retained">
          <NetworkIcon aria-hidden className="size-4 shrink-0" />
          <p className="min-w-0 flex-1">
            {s.retainedText(dev, retainedBy.attachments.map((a) => a.cidr).join(', '), retainedBy.name)}
          </p>
          <div className="shrink-0">
            <ReleaseAddressesButton
              deployment={deployment}
              disabledReason={disabledReason}
              onReleased={onReleased}
            />
          </div>
        </div>
      )}

      {detach && (
        <p className="callout callout-info mb-3" role="status" data-testid="network-detaching">
          <NetworkIcon aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            {s.detaching(detach.network)}{' '}
            {detach.runs[0] && (
              <>
                {s.detachRun}{' '}
                <Link className="link" to={`/runs/${detach.runs[0].id}`}>
                  {s.open}
                </Link>
              </>
            )}
            {detach.runs[1] && (
              <>
                {' · '}
                {s.thenApply(dev)}{' '}
                <Link className="link" to={`/runs/${detach.runs[1].id}`}>
                  {s.open}
                </Link>
              </>
            )}
          </span>
        </p>
      )}

      {editable ? (
        <fieldset
          className="network-mode"
          aria-describedby={disabledReason ? reasonId : undefined}
          data-testid="network-mode"
        >
          <legend className="sr-only">{s.modeLegend}</legend>
          <label className="network-radio">
            <input
              type="radio"
              name={radioName}
              checked={!chosen}
              aria-disabled={disabledReason ? true : undefined}
              onChange={() => setChoice(false)}
            />
            <span>
              <span className="font-semibold">{s.standalone}</span>
              <span className="text-fg-muted"> · {s.standaloneHelp}</span>
            </span>
          </label>
          <label className="network-radio">
            <input
              type="radio"
              name={radioName}
              checked={!!chosen}
              aria-disabled={disabledReason ? true : undefined}
              onChange={() => setChoice(true)}
            />
            <span className="font-semibold">{s.attach}</span>
          </label>
          {disabledReason && (
            <p id={reasonId} className="help">
              {disabledReason}
            </p>
          )}
        </fieldset>
      ) : (
        <dl className="settings-dl" data-testid="network-readonly">
          <dt>{s.network}</dt>
          <dd>{current ? current.name : s.readOnlyStandalone(dev)}</dd>
          {current && (
            <>
              <dt>{s.modeLabel}</dt>
              <dd>{s.mode[current.mode]}</dd>
            </>
          )}
        </dl>
      )}

      {editable && chosen && (
        <div className="network-choice">
          <div className="network-choice-fields">
            <div className="min-w-0 flex-1">
              <label className="label" htmlFor={`${ids}-network`}>
                {s.network}
              </label>
              <select
                id={`${ids}-network`}
                className="input w-full"
                value={chosen.networkId}
                aria-disabled={disabledReason ? true : undefined}
                aria-describedby={unavailable.length ? unavailableId : undefined}
                onChange={(e) => setNetwork(e.target.value)}
              >
                {!chosen.networkId && <option value="">{s.chooseNetwork}</option>}
                {items.map((item) => {
                  const reason = reasonFor(item);
                  return (
                    <option
                      key={item.id}
                      value={item.id}
                      disabled={!!reason}
                      aria-disabled={reason ? true : undefined}
                      title={reason ?? undefined}
                    >
                      {s.networkOption(
                        item.name,
                        s.topology[item.topology],
                        n.status[item.status].toLowerCase(),
                        whereText(item),
                      )}
                    </option>
                  );
                })}
              </select>
              {networks.data && items.length === 0 && <p className="help">{s.noNetworks}</p>}
              {!networks.data && <p className="help">{s.loadingNetworks}</p>}
              {unavailable.length > 0 && (
                <ul id={unavailableId} className="help network-unavailable">
                  {unavailable.map(({ item, reason }) => (
                    <li key={item.id}>{reason}</li>
                  ))}
                </ul>
              )}
            </div>
            {chosen.networkId && (
              <div className="network-size">
                <label className="label" htmlFor={`${ids}-size`}>
                  {s.size}
                </label>
                <select
                  id={`${ids}-size`}
                  className="input w-full"
                  value={size ?? ''}
                  aria-disabled={disabledReason ? true : undefined}
                  aria-describedby={`${ids}-size-help`}
                  onChange={(e) => setSize(e.target.value as AttachmentSize)}
                >
                  {!size && <option value="">{s.checking}</option>}
                  {SIZE_ORDER.map((value) => (
                    <option key={value} value={value}>
                      {value === recommended
                        ? s.recommendedOption(sizeOptionLabel(value))
                        : sizeOptionLabel(value)}
                    </option>
                  ))}
                </select>
                <div id={`${ids}-size-help`}>
                  {recommended && tight && (
                    <p className="help" data-testid="network-recommended">
                      {s.recommendedLine(dev, recommended, tight.role, tight.need, tight.capacity)}
                    </p>
                  )}
                  {tooSmall && size && (
                    <p className="callout callout-warning mt-1 text-sm" data-testid="network-too-small">
                      <TriangleAlert aria-hidden className="size-4 shrink-0" />
                      {s.smallerWarning(size, dev, tooSmall.role, tooSmall.need, tooSmall.capacity)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {chosen.networkId && (impact?.regions.length ?? 0) > 0 && (
            <details className="network-advanced">
              <summary>{s.advanced}</summary>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {impact!.regions.map((r) => {
                  const key = sizeKey(r.provider, r.region);
                  return (
                    <div key={key}>
                      <label className="label" htmlFor={`${ids}-size-${key}`}>
                        {s.sizeIn(providerLabel(r.provider), r.region)}
                      </label>
                      <select
                        id={`${ids}-size-${key}`}
                        className="input w-full"
                        value={chosen.sizes[key] ?? ''}
                        aria-disabled={disabledReason ? true : undefined}
                        onChange={(e) => setRegionSize(key, e.target.value as AttachmentSize | '')}
                      >
                        <option value="">{s.sameAsAbove}</option>
                        {SIZE_ORDER.map((value) => (
                          <option key={value} value={value}>
                            {sizeOptionLabel(value)}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      )}

      {shownDiagnostics.length > 0 && (
        <ul className="mt-3 space-y-1" data-testid="network-diagnostics">
          {shownDiagnostics.map((d, i) => {
            const info = networkErrorInfo(d.code, {
              network: chosenName || current?.name,
              deployment: dev,
              message: d.message,
            });
            const tone = d.severity === 'error' ? 'danger' : d.severity === 'warning' ? 'warning' : 'neutral';
            return (
              <li key={i} className={`network-diagnostic tone-${tone}`}>
                <span>{info?.sentence ?? d.message}</span> <code className="font-mono text-xs">{d.code}</code>
              </li>
            );
          })}
        </ul>
      )}

      {/* Addresses: what the change gives, else what the deployment has. */}
      {showImpact ? (
        <div className="mt-4">
          <h3 className="mb-1 text-sm font-semibold">{s.willGet(dev)}</h3>
          <AddressTable
            label={s.willGet(dev)}
            rows={impact!.regions.map((r) => {
              const reason = r.cidr ? null : staysReason(r.provider, r.region);
              return {
                key: sizeKey(r.provider, r.region),
                where: `${r.provider} · ${r.region}`,
                block: r.cidr,
                subnets: r.cidr ? subnetText(r.cidr) : (reason ?? ''),
                result: r.cidr ? (
                  <span className="badge tone-info">
                    <NetworkIcon aria-hidden className="size-3.5" />
                    {s.willAttach}
                  </span>
                ) : (
                  <span className="badge tone-warning">
                    <TriangleAlert aria-hidden className="size-3.5" />
                    {s.staysStandalone}
                  </span>
                ),
              };
            })}
          />
        </div>
      ) : current && !detaching ? (
        <div className="mt-4">
          <h3 className="mb-1 text-sm font-semibold">{s.addressesOf(dev)}</h3>
          <AddressTable
            label={s.addressesOf(dev)}
            rows={current.attachments.map((a) => ({
              key: sizeKey(a.provider, a.region),
              where: `${a.provider} · ${a.region}`,
              block: a.cidr,
              subnets: subnetText(a.cidr),
              result:
                current.mode === 'retained' ? (
                  <span className="badge tone-neutral">{n.retained}</span>
                ) : (
                  <StatusBadge presentation={attachmentPresentation(a.state)} />
                ),
            }))}
          />
        </div>
      ) : null}

      {chosen && impact?.requestChoices && impact.requestChoices.length > 0 && (
        <RequestTargets
          choices={impact.requestChoices}
          value={chosen.requestTargets ?? {}}
          disabled={!editable || !!disabledReason}
          diagnostics={diagnostics}
          onChange={(requestId, target) =>
            setForm((f) =>
              f.network
                ? {
                    ...f,
                    network: {
                      ...f.network,
                      requestTargets: Object.fromEntries(
                        Object.entries({ ...f.network.requestTargets, [requestId]: target }).filter(
                          ([, v]) => v,
                        ),
                      ),
                    },
                  }
                : f,
            )
          }
        />
      )}

      {editable && chosen?.networkId && impactPending && !impact && <p className="help mt-2">{s.checking}</p>}
      {editable && chosen?.networkId && impactFailed && <p className="help mt-2">{s.checkFailed}</p>}

      {showImpact && regionsWithBlocks.length > 0 && (
        <div className="callout callout-warning mt-3" role="status" data-testid="network-impact">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">
              {(impact!.change === 'resize' ? s.whatResizing : s.whatAttaching)(
                dev,
                regionsWithBlocks.map((r) => r.region).join(', '),
              )}
            </p>
            <ul className="mt-1 list-disc pl-5">
              {regionsWithBlocks.flatMap((r) => [
                // Feature spec 17, phase 4: an AWS attach moves the deployment's resources into its own VPC.
                ...(r.provider === 'aws' && impact!.change !== 'resize'
                  ? [
                      <li key={`${r.provider}/${r.region}/vpc`} data-testid="aws-attach-moves">
                        {s.awsAttachMoves(dev, r.region)}
                      </li>,
                    ]
                  : []),
                // Feature spec 17, phase 6: a GCP attach moves the resources into their own VPC and joins the hub.
                ...(r.provider === 'gcp' && impact!.change !== 'resize'
                  ? [
                      <li key={`${r.provider}/${r.region}/vpc`} data-testid="gcp-attach-moves">
                        {strings.networks.gcp.attachMoves(dev, r.region)}
                      </li>,
                    ]
                  : []),
                ...(r.interruption ? [<li key={`${r.provider}/${r.region}`}>{r.interruption}</li>] : []),
                ...r.replaced.map((x) => (
                  <li key={`${r.provider}/${r.region}/${x.resourceId}`}>
                    {s.replaced(x.resourceId, x.action)}
                  </li>
                )),
              ])}
              <li className="font-semibold" data-testid="network-queue">
                {impact!.queue.text}
                {impact!.queue.networkRunsAhead === 0 && ` ${s.fewMinutes}`}
              </li>
            </ul>
          </div>
        </div>
      )}

      {editable && detaching && !detach && attachedTo && (
        <div className="callout callout-warning mt-3" role="status" data-testid="network-detach">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold">{s.detachTitle(attachedTo.name)}</h3>
            <p className="mt-1">{s.detachText(dev, attachedTo.attachments.map((a) => a.cidr).join(', '))}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function AddressTable({
  label,
  rows,
}: {
  label: string;
  rows: { key: string; where: string; block: string | null; subnets: string; result: ReactNode }[];
}) {
  return (
    <div className="table-scroll">
      <table className="table network-addresses" aria-label={label}>
        <thead>
          <tr>
            <th scope="col">{s.columns.where}</th>
            <th scope="col">{s.columns.block}</th>
            <th scope="col">{s.columns.subnets}</th>
            <th scope="col">{s.columns.result}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className="whitespace-nowrap">{row.where}</td>
              <td className="font-mono">{row.block ?? strings.common.dash}</td>
              <td className={row.block ? 'font-mono text-xs' : 'text-sm'}>{row.subnets}</td>
              <td>{row.result}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
